import { chain, compact, startCase, noop, keyBy, startsWith } from "lodash"
import { VALUE_FORMATS, METRIC_TYPES, COMPOSITION_METRIC_IDS, ACCOUNT_CURRENCY_DESCRIPTORS, PERFORMANCE_CONTRIBUTION_CATEGORY_CURRENCY, PERFORMANCE_CONTRIBUTION_CATEGORY_PERCENT } from "./constants"
import { METRIC_OPTIONS } from "./definitions"
import { reorderByCategory, makeTooltipExtendedText, makeContributorIntradayPerformanceDelayTooltipText } from "./utils"

const DELTA = "delta"
const REBALANCED = "rebalanced"
const ORIGINAL_INSTRUMENT_COLUMN = "original"
// Companion of INTRADAY_PRICE_STREAM_METRIC_IDS (connected-components/asset-view/
// helpers/index.js): a priced column must appear in both — there by metric id,
// here by dataPath. A sync test in asset-view's __test__ guards the pairing.
export const INTRADAY_PRICE_COLUMN_DATA_PATHS = ["intraday.price", "intraday.change_percent"]

const SENSITIVITY_METRIC_CATEGORY_LABELS = {
  PREDICTEDFACTORSENSITIVITY: "Predicted Factor Sensitivity",
  HISTORICALFACTORSENSITIVITY: "Historical Factor Sensitivity",
}

function getCategoryName(category, modelMetadata, metricCategory, forecasts = {}, classifications = {}) {
  if (category === "impliedReturns") {
    return "Implied Returns"
  }

  const forecastCategory = forecasts[category]
  if (forecastCategory) {
    return forecastCategory.name
  }

  const classificationCategory = classifications[metricCategory]
  if (classificationCategory) {
    return classificationCategory.name
  }

  const sensitivityLabel = SENSITIVITY_METRIC_CATEGORY_LABELS[metricCategory]
  if (sensitivityLabel) {
    const categoryMetadata = modelMetadata?.categories?.find((c) => c.id === category)
    const modelCategoryLabel = categoryMetadata?.name || startCase(category)
    return `${sensitivityLabel} (${modelCategoryLabel})`
  }

  const categoryMetadata = modelMetadata.categories.find((c) => c.id === category)
  const categoryLabel = categoryMetadata?.name || startCase(category)

  // If both labels are the same, we don't want to have a duplicate label - e.g. "Descriptors (DESCRIPTORS)"
  if (metricCategory && metricCategory.toLowerCase() !== categoryLabel.toLowerCase()) {
    return `${categoryLabel} (${metricCategory})`
  }
  return categoryLabel
}

function resolveAssetViewMetricCategoryName(metricId) {
  const [prefix, subCategory] = metricId.split(".")
  const displayType = metricId.split(".").at(-1)

  if (prefix === "performance" && subCategory === "summary") {
    return displayType === METRIC_TYPES.CUMULATIVE_PERCENT
      ? PERFORMANCE_CONTRIBUTION_CATEGORY_PERCENT
      : PERFORMANCE_CONTRIBUTION_CATEGORY_CURRENCY
  }

  if (prefix === "risk" && subCategory === "summary") {
    return "Risk Decomposition"
  }

  if (subCategory === "factorAggregates") {
    if (prefix === "risk") {
      return "Risk Factors"
    }
    if (prefix === "performance") {
      return "Performance Factors"
    }
  }

  if (COMPOSITION_METRIC_IDS.includes(metricId)) {
    return "Composition"
  }

  return null
}

export function resolveContentSetCategoryLabel(categoryId, contentSetCategories) {
  if (!categoryId) {
    return categoryId
  }

  const matchedCategory = contentSetCategories?.find(
    ({ id }) => id.toLowerCase() === String(categoryId).toLowerCase(),
  )

  return matchedCategory?.name || startCase(categoryId)
}

function getContentSetCategoryNameFromMetricId(metricId, contentSets) {
  if (!contentSets?.length) {
    return null
  }

  const [contentSetId, categoryId] = metricId.split(".")
  const contentSet = contentSets.find(({ id }) => id === contentSetId)
  if (!contentSet || !categoryId) {
    return null
  }

  return resolveContentSetCategoryLabel(categoryId, contentSet.categories)
}

function doesContentSetMetricKeyMatch(metricKey, metricValue, {
  contentSetId,
  selectedCategory,
  selectedMetricDisplay,
  requireFactorSegment = false,
}) {
  if (!contentSetId || !selectedCategory) {
    return false
  }

  const [prefix, categorySegment, factorSegment, metricDisplay] = metricKey.split(".")
  if (prefix !== contentSetId) {
    return false
  }

  if (requireFactorSegment && (!categorySegment || !factorSegment)) {
    return false
  }

  const metricCategory = metricValue?.category
  if (!metricCategory) {
    return false
  }

  if (metricCategory.toLowerCase() !== selectedCategory.toLowerCase()) {
    return false
  }

  if (!selectedMetricDisplay) {
    return true
  }

  return metricDisplay === selectedMetricDisplay
}

function filterOutDeltaStrings(format, instrumentColumns) {
  if (format === VALUE_FORMATS.STRING) {
    return instrumentColumns.filter(column => column !== DELTA)
  }
  return instrumentColumns
}

const makeStringColumnFilter = ({ format }) =>
  (instrumentColumns) => filterOutDeltaStrings(format, instrumentColumns)

const customInstrumentFilters = {
  performance: ({ isEndVsStartDateComparison }) =>
    getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison),
  descriptors: makeStringColumnFilter,
  attributes: makeStringColumnFilter,
}

function shouldUseSharedIntradayColumns(dataPath) {
  if (INTRADAY_PRICE_COLUMN_DATA_PATHS.includes(dataPath)) return true
  if (typeof dataPath !== "string") return false
  return dataPath.startsWith("intradayContribution") || dataPath.startsWith("intradayStandalone")
}

// Contribution: every compare column (A, B, delta). Price / standalone: one primary column only.
function makeIntradayInstrumentColumnsForCompare(dataPath, instrumentColumns = []) {
  if (instrumentColumns.length <= 1) {
    return instrumentColumns
  }
  if (typeof dataPath === "string" && dataPath.startsWith("intradayContribution")) {
    return instrumentColumns
  }
  return instrumentColumns.includes(ORIGINAL_INSTRUMENT_COLUMN)
    ? [ORIGINAL_INSTRUMENT_COLUMN]
    : [instrumentColumns[0]]
}

function makeIntradayMetricColumnFilter(dataPath) {
  return () => (instrumentColumns) => makeIntradayInstrumentColumnsForCompare(dataPath, instrumentColumns)
}

export function getCustomSelectedMetrics(selectedMetrics, flattenedMetrics, modelMetadata, instrumentsWithMetadata, forecasts, classifications, isEndVsStartDateComparison, contentSets = []) {
  if (!selectedMetrics) {
    return []
  }
  const customMetrics = selectedMetrics.map((metricId) => {
    let id = metricId
    let modelId
    if (startsWith(metricId, "model")) {
      // Example: model.<modelId>.<metric.path>  →  e.g. "model.AXWW4-MH.risk.country.US"  →  modelId="AXWW4-MH", id="risk.country.US"
      const splitId = metricId.split(".")
      modelId = splitId[1]
      id = splitId.slice(2).join(".")
    }

    if (modelId && modelMetadata && modelId !== modelMetadata.id) {
      return null
    }

    const metric = flattenedMetrics?.[id] ?? {}
    const {
      dataPath,
      category,
      label,
      format,
      metricCategory,
      prefix,
      enhanceValue,
      downloadDataGetter,
      tooltipMetricType,
      hideInstrumentLabel,
    } = metric
    const categoryName = getContentSetCategoryNameFromMetricId(id, contentSets)
      ?? resolveAssetViewMetricCategoryName(id)
      ?? getCategoryName(category, modelMetadata, metricCategory, keyBy(forecasts, "id"), keyBy(classifications, "id"))
    const filterInstrumentColumns =
      customInstrumentFilters[prefix]
      || (shouldUseSharedIntradayColumns(dataPath) ? makeIntradayMetricColumnFilter(dataPath) : noop)

    return ({
      id,
      label: label || metricId,
      category: categoryName,
      dataPath,
      prefix: metric.prefix,
      format: format || VALUE_FORMATS.NUMBER,
      filterInstrumentColumns: filterInstrumentColumns({ isEndVsStartDateComparison, format }),
      enhanceValue,
      downloadDataGetter,
      tooltipText: (instrumentType) => makeTooltipExtendedText(dataPath, instrumentType, metric, instrumentsWithMetadata),
      ...(tooltipMetricType != null && { tooltipMetricType }),
      ...(hideInstrumentLabel && { hideInstrumentLabel }),
      ...(metric.isPerformanceMetric && { isPerformanceMetric: true }),
      ...modelId && { modelId },
    })
  })
  return reorderByCategory(compact(customMetrics))
}

const INTRADAY_DELAY_TAG = "15m"
const INTRADAY_PRICE_DATA_PATH = "intraday.price"
const PRICE_DELAY_TOOLTIP = "Prices 15 minutes delayed."
const SINCE_START_OF_DAY_DELAY_TOOLTIP = "Since start of day, using prices 15 minutes delayed."
const USING_PRICES_DELAY_TOOLTIP = "Using prices 15 minutes delayed."
const MULTI_DAY_INTRADAY_PERFORMANCE_PATH = /^intraday(Contribution|Standalone)1[wm]\./

export function contributorMetricLogicalDataPath(metric) {
  return metric.baseDataPath ?? metric.dataPath
}

const ACCOUNT_CURRENCY_DATA_PATHS = ACCOUNT_CURRENCY_DESCRIPTORS.map((descriptor) => `descriptors.${descriptor}`)

export function isAccountCurrencyMetric(metric) {
  return ACCOUNT_CURRENCY_DATA_PATHS.includes(contributorMetricLogicalDataPath(metric))
}

function metricWithIntradayDelayTooltip(metric, instrumentsWithMetadata) {
  const path = contributorMetricLogicalDataPath(metric)
  if (INTRADAY_PRICE_COLUMN_DATA_PATHS.includes(path)) {
    const delayText = path === INTRADAY_PRICE_DATA_PATH ? PRICE_DELAY_TOOLTIP : SINCE_START_OF_DAY_DELAY_TOOLTIP
    return {
      ...metric,
      delayTag: INTRADAY_DELAY_TAG,
      tooltipText: [delayText, <br key="br" />, metric.category],
    }
  }
  const delayText = MULTI_DAY_INTRADAY_PERFORMANCE_PATH.test(path) ? USING_PRICES_DELAY_TOOLTIP : SINCE_START_OF_DAY_DELAY_TOOLTIP
  const tooltipText = makeContributorIntradayPerformanceDelayTooltipText(metric, delayText, instrumentsWithMetadata)
  return {
    ...metric,
    delayTag: INTRADAY_DELAY_TAG,
    ...(tooltipText && { tooltipText }),
  }
}

export function enrichMetricsWithIntradayDelayTooltips(metrics, { instrumentsWithMetadata } = {}) {
  if (!metrics?.length) {
    return metrics
  }
  return metrics.map((metric) => (
    shouldUseSharedIntradayColumns(contributorMetricLogicalDataPath(metric))
      ? metricWithIntradayDelayTooltip(metric, instrumentsWithMetadata)
      : metric
  ))
}

// selectedMetricDisplay null means "show every non-disabled metric type as its own column"
// (netContribution / currencyExposure / securityExposure — whichever apply to this content
// set) rather than collapsing to one. Each needs a distinct suffix or every column for the
// same content-set metric renders the identical bare name, indistinguishable from the others.
const CONTENT_SET_METRIC_LABEL_SUFFIXES = {
  netContribution: "contribution",
  currencyExposure: "currency exposure",
  securityExposure: "Raw asset score",
}

export function getContentSetMetrics(contentSetId, flattenedMetrics, selectedCategory, selectedMetricDisplay, contentSetCategories) {
  if (!contentSetId) {
    return []
  }

  // Filter flattened metrics to only include keys that start with the contentSetId
  // and then transform that metric to have the correct metadata
  const filteredMetrics = Object.entries(flattenedMetrics)
    .reduce((acc, [key, value]) => {
      if (!doesContentSetMetricKeyMatch(key, value, {
        contentSetId,
        selectedCategory,
        selectedMetricDisplay,
      })) {
        return acc
      }

      const [prefix, category, factor] = key.split(".")

      const dataKey = selectedMetricDisplay
        ? compact([prefix, category, factor]).join(".")
        : key

      if (dataKey && !acc[dataKey]) {
        const baseLabel = chain(value.label)
          .split(" (")
          .tap(array => array.pop())
          .get(0)
          .value()
        // Only add a metric-type suffix in "every metric type" mode (selectedMetricDisplay
        // null) — callers that pass a concrete selectedMetricDisplay (e.g. the Asset View
        // content-set table) already resolve to one unambiguous column and expect the bare
        // content-set-metric name, unchanged from before dual mode existed.
        const suffix = !selectedMetricDisplay && CONTENT_SET_METRIC_LABEL_SUFFIXES[value.id]
        const label = suffix ? `${baseLabel} ${suffix}` : baseLabel

        acc[dataKey] = {
          ...value,
          label,
          dataPath: dataKey,
        }
      }
      return acc
    }, {})

  const contentSetMetrics = Object.entries(filteredMetrics).map(([metricId, metricData]) => {
    const { dataPath, label, format, category } = metricData
    return ({
      id: metricId,
      label: label || metricId,
      category: contentSetCategories
        ? resolveContentSetCategoryLabel(selectedCategory || category, contentSetCategories)
        : (selectedCategory || category),
      dataPath,
      format: format || VALUE_FORMATS.NUMBER,
    })
  })
  return reorderByCategory(contentSetMetrics)
}

export function getContentSetDuplicateMetricIds(contentSetId, flattenedMetrics, selectedCategory, selectedMetricDisplay) {
  if (!contentSetId || !flattenedMetrics || !selectedCategory) {
    return []
  }

  return Object.keys(flattenedMetrics).filter((metricKey) => doesContentSetMetricKeyMatch(
    metricKey,
    flattenedMetrics[metricKey],
    {
      contentSetId,
      selectedCategory,
      selectedMetricDisplay,
      requireFactorSegment: true,
    },
  ))
}

// Always filter out rebalanced and delta columns from all performance metrics
// and filter out start/comparison and delta columns for end/start comparison
export function getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison) {
  return (modeColumns) => {
    const filteredColumns = modeColumns.length > 1 && modeColumns.includes(REBALANCED) ? modeColumns.filter(column => ![REBALANCED, DELTA].includes(column)) : modeColumns
    if (isEndVsStartDateComparison) {
      return ["original"]
    }
    return filteredColumns
  }
}

export function flattenMetrics(items, metricPathPrefix = "", category = null, label = null) {
  return items.reduce((acc, item) => {
    const categoryForChildren = item.items
      ? (item.category || item.id)
      : category
    const metricPathPrefixForChildren = item.excludeGroupIdFromChildMetricPaths
      ? metricPathPrefix
      : compact([metricPathPrefix, item.id]).join(".")
    if ((item.items || item.metricTypeOptions)) {
      return {
        ...acc,
        ...flattenMetrics(
          (item.items || item.metricTypeOptions),
          metricPathPrefixForChildren,
          categoryForChildren,
          item.metricTypeOptions ? item.label : label,
        ),
      }
    }

    const flattenedMetricId = compact([metricPathPrefix, item.id]).join(".")

    let compositeLabel = label
    if (label && item.label) {
      compositeLabel = `${compositeLabel} (${item.label})`
    } else if (item.label) {
      compositeLabel = item.label
    }

    const metricCategory = chain(flattenedMetricId)
      .split(".")
      .first()
      .toUpper()
      .value()

    return {
      ...acc,
      [flattenedMetricId]: {
        ...item,
        label: compositeLabel,
        metricCategory,
        ...(category && { category }),
      },
    }
  }, {})
}

export function getDefaultMetricDisplay(metricCategory) {
  const options = METRIC_OPTIONS[metricCategory] || []
  const defaultMetric = options.find(({ isDefault }) => isDefault)
  return defaultMetric?.id || options[0]?.id
}
